// Standalone SVG builders for The Raven's Quill emblem and lockup, plus export
// helpers (SVG file + transparent PNG). Mirrors the QuillLogo component paths.

import { LIBRE_BASKERVILLE_ITALIC_WOFF2, LIBRE_BASKERVILLE_REGULAR_WOFF2 } from "./lockupFonts";
import { FEATHER_PATH, DROP_PATH, DROP_TRANSFORM, FEATHER_TRANSFORM } from "./featherPaths";

// Libre Baskerville embedded as @font-face so the downloadable SVG (and any PNG
// converted from it) renders the wordmark and tagline with the correct font on
// any machine, offline, and on the deployed live site.
const FONT_DEFS = `<defs><style>@font-face{font-family:'RQ Serif';font-style:italic;font-weight:400;src:url(data:font/woff2;base64,${LIBRE_BASKERVILLE_ITALIC_WOFF2}) format('woff2');}@font-face{font-family:'RQ Serif';font-style:normal;font-weight:400;src:url(data:font/woff2;base64,${LIBRE_BASKERVILLE_REGULAR_WOFF2}) format('woff2');}</style></defs>`;

const PRESETS = {
  ink: { shaft: "#1C1A17", nib: "#7A2E2E", slit: "#4A1F1F", word: "#1C1A17", accent: "#7A2E2E" },
  // "gold" key retained for compatibility, now renders a monochrome Apple-style
  // silver/gray for the feather on dark backgrounds.
  gold: { shaft: "#7E837E", nib: "#63675F", slit: "#4E524E", word: "#C2C7CC", accent: "#C2C7CC" },
  mono: { shaft: "#1C1A17", nib: "#1C1A17", slit: "#000000", word: "#1C1A17", accent: "#1C1A17" },
  white: { shaft: "#FFFFFF", nib: "#FFFFFF", slit: "#FFFFFF", word: "#FFFFFF", accent: "#FFFFFF" },
};

// Aged matte-pewter gradient stops for the reversed (dark-background) feather:
// a soft satin highlight sinking into tarnished, deep-oxidized tones, as if the
// metal has settled into the paper over decades. Slightly warm-neutral, low sheen.
const PEWTER_STOPS = `<stop offset="0" stop-color="#B6B8AF"/><stop offset="0.2" stop-color="#9DA098"/><stop offset="0.45" stop-color="#82867F"/><stop offset="0.72" stop-color="#666a63"/><stop offset="0.9" stop-color="#565a53"/><stop offset="1" stop-color="#494c46"/>`;

// Diagonal feather-quill emblem, vector-traced from the client artwork. The
// blade takes the shaft colour; the ink drop at the nib takes the accent (nib)
// colour. Rendered in a 1337 x 848 box (wide, diagonal). The potrace transform
// maps the traced pixel-space paths into that box.
function emblemInner(variant) {
  const p = PRESETS[variant] || PRESETS.ink;
  if (variant === "gold") {
    const u = Math.random().toString(36).slice(2, 9);
    const gid = "g" + u, cid = "c" + u, fid = "f" + u;
    return `<defs>
      <linearGradient id="${gid}" gradientUnits="objectBoundingBox" x1="0" y1="0" x2="0.92" y2="1">${PEWTER_STOPS}</linearGradient>
      <clipPath id="${cid}"><path d="${FEATHER_PATH}" transform="${FEATHER_TRANSFORM}"/></clipPath>
      <filter id="${fid}"><feTurbulence type="fractalNoise" baseFrequency="0.11" numOctaves="2" seed="11" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope="0.22" intercept="0"/></feComponentTransfer></filter>
    </defs>
    <g transform="${FEATHER_TRANSFORM}">
      <path d="${FEATHER_PATH}" fill="url(#${gid})" stroke="none"/>
      <path d="${DROP_PATH}" transform="${DROP_TRANSFORM}" fill="#B0573F" stroke="none"/>
    </g>
    <rect x="0" y="0" width="1337" height="848" clip-path="url(#${cid})" filter="url(#${fid})"/>`;
  }
  return `<g transform="${FEATHER_TRANSFORM}">
    <path d="${FEATHER_PATH}" fill="${p.shaft}" stroke="none"/>
    <path d="${DROP_PATH}" transform="${DROP_TRANSFORM}" fill="${p.nib}" stroke="none"/>
  </g>`;
}

export const EMBLEM_VIEWBOX = "0 0 1337 848";
export const EMBLEM_ASPECT = 1337 / 848; // ~1.577 (wide)

export function buildEmblemSvg(variant = "ink") {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1337 848" fill="none" role="img" aria-label="The Raven's Quill emblem" style="width:100%;height:auto;display:block">${emblemInner(
    variant
  )}</svg>`;
}

// Full lockup: emblem centred above the wordmark, a line centred between the
// wordmark and the tagline. Colours can be overridden (e.g. for the dark card
// back). Fonts are embedded so it rasterises identically in html2canvas exports.
export function buildLockupSvg(variant = "ink", opts = {}) {
  const p = PRESETS[variant] || PRESETS.ink;
  const word = opts.word || p.word;
  const accent = opts.accent || p.accent;
  const tagColor = opts.tagColor || accent;
  const emblemTransform = opts.emblemTransform || "translate(160,18) scale(0.1795)";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 340" fill="none" role="img" aria-label="The Raven's Quill" style="width:100%;height:auto">${FONT_DEFS}
    <g transform="${emblemTransform}">${emblemInner(variant)}</g>
    <text x="280" y="248" text-anchor="middle" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-style="italic" font-size="46" fill="${word}">The Raven&#8217;s Quill</text>
    <line x1="150" y1="280" x2="410" y2="280" stroke="${accent}" stroke-width="1.4"/>
    <text x="280" y="318" text-anchor="middle" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-size="13" letter-spacing="5" fill="${tagColor}">BESPOKE LEGACY PRESERVATION</text>
  </svg>`;
}

// Compact stacked lockup for the site header: the feather sits directly above
// the wordmark so its nib points down toward the writing. Tighter than the full
// stacked lockup (smaller tagline, less vertical air).
export function buildLockupStackedCompactSvg(variant = "ink", opts = {}) {
  const p = PRESETS[variant] || PRESETS.ink;
  const word = opts.word || p.word;
  const accent = opts.accent || p.accent;
  const tagColor = opts.tagColor || accent;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 214" fill="none" role="img" aria-label="The Raven's Quill" style="width:100%;height:auto;display:block">${FONT_DEFS}
    <g transform="translate(196,4) scale(0.1287)">${emblemInner(variant)}</g>
    <text x="280" y="158" text-anchor="middle" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-style="italic" font-size="44" fill="${word}">The Raven&#8217;s Quill</text>
    <line x1="176" y1="180" x2="384" y2="180" stroke="${accent}" stroke-width="1.3"/>
    <text x="280" y="204" text-anchor="middle" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-size="11.5" letter-spacing="4.5" fill="${tagColor}">BESPOKE LEGACY PRESERVATION</text>
  </svg>`;
}


// Circular monogram seal: "Z / P" initials at the centre with "BESPOKE LEGACY"
// arced over the top and "PRESERVATION" arced under the bottom (stamp style).
// Transparent, single-colour, for social media avatars and watermarks.
export function buildBadgeSvg(variant = "ink") {
  const c = variant === "white" ? "#FFFFFF" : variant === "black" ? "#000000" : "#1C1A17";
  const cx = 200, cy = 200, R = 126;
  const topPath = `M 81.6,243.1 A ${R},${R} 0 1 1 318.4,243.1`;
  const bottomPath = `M ${cx + R},${cy} A ${R},${R} 0 0 1 ${cx - R},${cy}`;
  const serif = "'RQ Serif', 'Libre Baskerville', Georgia, serif";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" fill="none" role="img" aria-label="The Raven's Quill monogram" style="width:100%;height:auto;display:block">${FONT_DEFS}
    <defs>
      <path id="rq-badge-top" d="${topPath}"/>
      <path id="rq-badge-bottom" d="${bottomPath}"/>
    </defs>
    <text fill="${c}" font-family="${serif}" font-size="22" letter-spacing="4"><textPath href="#rq-badge-top" startOffset="27.48%" text-anchor="middle">BESPOKE</textPath></text>
    <text fill="${c}" font-family="${serif}" font-size="22" letter-spacing="4"><textPath href="#rq-badge-top" startOffset="74.40%" text-anchor="middle">LEGACY</textPath></text>
    <text fill="${c}" font-family="${serif}" font-size="22" letter-spacing="4"><textPath href="#rq-badge-bottom" startOffset="50%" text-anchor="middle">PRESERVATION</textPath></text>
    <path d="M 200 69 L 205 74 L 200 79 L 195 74 Z" fill="${c}"/>
    <path d="M 323.68 219.04 L 328.68 224.04 L 323.68 229.04 L 318.68 224.04 Z" fill="${c}"/>
    <path d="M 76.32 219.04 L 81.32 224.04 L 76.32 229.04 L 71.32 224.04 Z" fill="${c}"/>
    <g transform="translate(0,30)">
      <text x="150" y="185" font-family="${serif}" font-size="112" fill="${c}" text-anchor="middle">Z</text>
      <line x1="182" y1="252" x2="228" y2="128" stroke="${c}" stroke-width="3.2" stroke-linecap="round"/>
      <text x="252" y="262" font-family="${serif}" font-size="112" fill="${c}" text-anchor="middle">P</text>
    </g>
  </svg>`;
}

// Oval monogram: "Z / P" initials framed by a thin portrait ellipse. Elegant,
// minimal social avatar / watermark. Transparent, single-colour.
export function buildOvalMonogramSvg(variant = "ink") {
  const c = variant === "white" ? "#FFFFFF" : variant === "black" ? "#000000" : "#1C1A17";
  const serif = "'RQ Serif', 'Libre Baskerville', Georgia, serif";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" fill="none" role="img" aria-label="The Raven's Quill monogram" style="width:100%;height:auto;display:block">${FONT_DEFS}
    <ellipse cx="200" cy="200" rx="134" ry="174" fill="none" stroke="${c}" stroke-width="1.6"/>
    <g transform="translate(0,16)">
      <text x="150" y="185" font-family="${serif}" font-size="112" fill="${c}" text-anchor="middle">Z</text>
      <line x1="182" y1="252" x2="228" y2="128" stroke="${c}" stroke-width="3.2" stroke-linecap="round"/>
      <text x="252" y="262" font-family="${serif}" font-size="112" fill="${c}" text-anchor="middle">P</text>
    </g>
  </svg>`;
}



// Horizontal lockup: emblem on the left, wordmark + underline + tagline stacked
// to the right. Mirrors the site header / colophon arrangement.
export function buildLockupHorizontalSvg(variant = "ink") {
  const p = PRESETS[variant] || PRESETS.ink;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 620 190" fill="none" role="img" aria-label="The Raven's Quill" style="width:100%;height:auto;display:block">${FONT_DEFS}
    <g transform="translate(6,40) scale(0.125)">${emblemInner(variant)}</g>
    <text x="192" y="96" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-style="italic" font-size="40" fill="${p.word}">The Raven&#8217;s Quill</text>
    <line x1="192" y1="112" x2="427" y2="112" stroke="${p.accent}" stroke-width="1.1"/>
    <text x="192" y="140" font-family="'RQ Serif', 'Libre Baskerville', Georgia, serif" font-size="11.5" letter-spacing="4" fill="${p.accent}">BESPOKE LEGACY PRESERVATION</text>
  </svg>`;
}

// Shared display width (px) for the stacked lockup colophon, so the mark is
// pixel-identical across every downloadable (both writing samples + the
// prospectus footer). Change once here to resize everywhere.
export const STACKED_COLOPHON_WIDTH = 190;


export function downloadText(filename, text, mime = "image/svg+xml") {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  triggerDownload(filename, URL.createObjectURL(blob));
}

export function svgToPng(svgString, width, height) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, width, height); // transparent background
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.onerror = reject;
    img.src = url;
  });
}

export async function downloadPng(svgString, filename, width, height) {
  const blob = await svgToPng(svgString, width, height);
  triggerDownload(filename, URL.createObjectURL(blob));
}

function triggerDownload(filename, url) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
